import React, { useContext } from "react";
import { FormValidator } from "@root/_utils";
import type {
  ColumnConfig,
  ColumnTypeConfig,
  DataEditorProps,
  DataViewerProps,
} from "@gistapps/composite-spreadsheet";
import { SpreadsheetContext } from "@gistapps/composite-spreadsheet";
import DropdownCellEditor from "@root/_components/BulkSpreadsheetEditors/DropdownCellEditor";
import type { DropdownOption } from "@root/_components/BulkSpreadsheetEditors/DropdownCellEditor";
import DateTimeCellEditor from "@root/_components/BulkSpreadsheetEditors/DateTimeCellEditor";
import PhoneCellEditor from "@root/_components/BulkSpreadsheetEditors/PhoneCellEditor";
import ImageCellViewer from "@root/_components/BulkSpreadsheetEditors/ImageCellViewer";
import SendNowCellViewer from "@root/_components/BulkSpreadsheetEditors/SendNowCellViewer";
import IdCellViewer from "@root/_components/BulkSpreadsheetEditors/IdCellViewer";
import type { VariantOption } from "@root/_components/BulkSpreadsheetEditors/IdCellViewer";
import DefaultCellViewer from "@root/_components/BulkSpreadsheetEditors/DefaultCellViewer";

const makeSendOnEditor = (deliveryTimeKey: string) => {
  const Editor = (props: DataEditorProps) => {
    const { columnMap, getSpreadsheetCellData } = useContext(SpreadsheetContext);
    const dtCol = columnMap.get(deliveryTimeKey);
    const dtValue = dtCol !== undefined ? getSpreadsheetCellData(props.row, dtCol)?.value : undefined;
    return (dtValue === "Send Later") ? <DateTimeCellEditor {...props} /> : null;
  };
  return Editor;
};

const makeSendOnViewer = (deliveryTimeKey: string) => {
  const Viewer = (props: DataViewerProps) => {
    const { columnMap, getSpreadsheetCellData } = useContext(SpreadsheetContext);
    const dtCol = columnMap.get(deliveryTimeKey);
    const dtValue = dtCol !== undefined ? getSpreadsheetCellData(props.row, dtCol)?.value : undefined;
    return (dtValue === "Send Later") ? <DefaultCellViewer {...props} /> :
      <div className="Spreadsheet__data-viewer rss__data-viewer">{"\u00A0"}</div>;
  };
  return Viewer;
};

const makeConditionalDropdownEditor = (deliveryTimeKey: string, options: DropdownOption[]) => {
  const Editor = (props: DataEditorProps) => {
    const { columnMap, getSpreadsheetCellData } = useContext(SpreadsheetContext);
    const dtCol = columnMap.get(deliveryTimeKey);
    const dtValue = dtCol !== undefined ? getSpreadsheetCellData(props.row, dtCol)?.value : undefined;
    return (dtValue === "Send Later") ? <DropdownCellEditor {...props} options={options} /> : null;
  };
  return Editor;
};

type ProxyBulkOrderColumnsResult = {
  columns: ColumnConfig[];
  columnTypeConfig: ColumnTypeConfig;
};

/**
 * Build composite-spreadsheet column config for the proxy (storefront) bulk order table.
 * Uses settings.translations.bulk.* for labels (customer-facing theme translations).
 */
export function getProxyBulkOrderColumns(
  settings: any,
  amountChoices: { values: Array<{ value: string; label: string }> },
): ProxyBulkOrderColumnsResult {

  const t = settings.translations;

  const variantOptions: VariantOption[] = (amountChoices?.values ?? []).map((v) => ({
    value: v.value,
    name: v.label,
  }));

  const variantDropdownOptions: DropdownOption[] = [
    { name: t.bulk.select_amount, value: "" },
    ...variantOptions.map((v) => ({ name: v.name, value: v.value })),
  ];

  const makeDropdownEditor = (options: DropdownOption[]) =>
    (props: DataEditorProps) => <DropdownCellEditor {...props} options={options} />;

  const makeIdViewer = (options: VariantOption[]) =>
    (props: DataViewerProps) => <IdCellViewer {...props} options={options} />;

  const SEND_NOW_OPTIONS: DropdownOption[] = [
    { name: t.bulk.select_option, value: "" },
    { name: t.purchase.send_now_label, value: "Send Now" },
    { name: t.purchase.send_later_label, value: "Send Later" },
  ];

  const TIMEZONE_OPTIONS: DropdownOption[] = [
    { name: t.bulk.select_option, value: "" },
    { name: "-12:00", value: "-12:00" },
    { name: "-11:00", value: "-11:00" },
    { name: "-10:00", value: "-10:00" },
    { name: "-09:30", value: "-09:30" },
    { name: "-09:00", value: "-09:00" },
    { name: "-08:00", value: "-08:00" },
    { name: "-07:00", value: "-07:00" },
    { name: "-06:00", value: "-06:00" },
    { name: "-05:00", value: "-05:00" },
    { name: "-04:30", value: "-04:30" },
    { name: "-04:00", value: "-04:00" },
    { name: "-03:30", value: "-03:30" },
    { name: "-03:00", value: "-03:00" },
    { name: "-02:00", value: "-02:00" },
    { name: "-01:00", value: "-01:00" },
    { name: "+00:00", value: "+00:00" },
    { name: "+01:00", value: "+01:00" },
    { name: "+02:00", value: "+02:00" },
    { name: "+03:00", value: "+03:00" },
    { name: "+03:30", value: "+03:30" },
    { name: "+04:00", value: "+04:00" },
    { name: "+04:30", value: "+04:30" },
    { name: "+05:00", value: "+05:00" },
    { name: "+05:30", value: "+05:30" },
    { name: "+05:45", value: "+05:45" },
    { name: "+06:00", value: "+06:00" },
    { name: "+06:30", value: "+06:30" },
    { name: "+07:00", value: "+07:00" },
    { name: "+08:00", value: "+08:00" },
    { name: "+08:45", value: "+08:45" },
    { name: "+09:00", value: "+09:00" },
    { name: "+09:30", value: "+09:30" },
    { name: "+10:00", value: "+10:00" },
    { name: "+10:30", value: "+10:30" },
    { name: "+11:00", value: "+11:00" },
    { name: "+11:30", value: "+11:30" },
    { name: "+12:00", value: "+12:00" },
    { name: "+12:45", value: "+12:45" },
    { name: "+13:00", value: "+13:00" },
    { name: "+14:00", value: "+14:00" },
  ];

  const columns: ColumnConfig[] = [
    {
      type: "custom",
      key: "id",
      label: t.bulk.amount_label,
      DataEditor: makeDropdownEditor(variantDropdownOptions),
      DataViewer: makeIdViewer(variantOptions),
      required: true,
      onValidate: (value: string) => {
        const validIds = variantOptions.map((v) => String(v.value));
        return FormValidator.constraints.isMemberOf(String(value ?? ""), validIds);
      },
    },
    {
      type: "custom",
      key: "properties___gcp_selected_card_image",
      label: t.purchase.card_image_breadcrumb,
      DataEditor: () => null,
      DataViewer: (props: DataViewerProps) => <ImageCellViewer {...props} />,
      required: true,
      onValidate: () => true,
    },
    {
      type: "custom",
      key: "properties___gcp_recipient_email",
      label: t.bulk.recipient_email,
      onValidate: (value: string) => FormValidator.constraints.isEmail(value),
    },
    {
      type: "custom",
      key: "properties___gcp_recipient_phone",
      label: t.bulk.recipient_phone,
      DataEditor: (props: DataEditorProps) => <PhoneCellEditor {...props} />,
      onValidate: (value: string) => (
        !value ? true : FormValidator.constraints.isPhone(value)
      ),
    },
    {
      type: "custom",
      key: "properties___gcp_recipient_name",
      label: t.bulk.recipient_name,
      onValidate: (value: string) => FormValidator.constraints.hasValue(value),
    },
    {
      type: "custom",
      key: "properties___gcp_delivery_time",
      label: t.purchase.delivery_date_heading,
      DataEditor: makeDropdownEditor(SEND_NOW_OPTIONS),
      DataViewer: (props: DataViewerProps) => <SendNowCellViewer {...props} />,
      onValidate: (value: string) => FormValidator.constraints.isMemberOf(value, ["Send Now", "Send Later"]),
    },
    {
      type: "custom",
      key: "properties___gcp_delivery_date",
      label: t.bulk.deliver_on_label,
      DataEditor: makeSendOnEditor("properties___gcp_delivery_time"),
      DataViewer: makeSendOnViewer("properties___gcp_delivery_time"),
      onValidate: (value: string, rowData: any[]) => {
        const dtValue = rowData[5]?.value;
        if (dtValue !== "Send Later") return true;
        if (!value || value === "") return false;
        return FormValidator.constraints.isDate(value);
      },
    },
    {
      type: "custom",
      key: "properties___gcp_delivery_timezone",
      label: t.bulk.timezone_label,
      DataEditor: makeConditionalDropdownEditor("properties___gcp_delivery_time", TIMEZONE_OPTIONS),
      DataViewer: makeSendOnViewer("properties___gcp_delivery_time"),
      onValidate: (value: string, rowData: any[]) => {
        const dtValue = rowData[5]?.value;
        if (dtValue !== "Send Later") return true;
        if (!value || value === "") return false;
        return TIMEZONE_OPTIONS.some((opt) => opt.value !== "" && opt.value === value);
      },
    },
    {
      type: "custom",
      key: "properties___gcp_message_from",
      label: t.purchase.from_label,
      onValidate: (value: string) => FormValidator.constraints.hasValue(value),
    },
    {
      type: "custom",
      key: "properties___gcp_message_text",
      label: t.bulk.gift_message ?? "Gift Message",
      onValidate: () => true,
    },
  ];

  if (settings.global?.appearance?.allow_user_salutation_selection) {

    const SALUTATION_OPTIONS: DropdownOption[] = [
      { name: t.bulk.select_option, value: "" },
      { name: t.purchase.salutation1, value: "Dear" },
      { name: t.purchase.salutation2, value: "Hi" },
      { name: t.purchase.salutation3, value: "Hello" },
      { name: t.purchase.salutation4, value: "Hey" },
    ];

    columns.push({
      type: "custom",
      key: "properties___gcp_message_salutation",
      label: t.bulk.salutation ?? "Salutation",
      DataEditor: makeDropdownEditor(SALUTATION_OPTIONS),
      onValidate: () => true,
    });

  }

  if (settings.global?.appearance?.allow_user_font_selection) {

    const fontValues: DropdownOption[] = [
      { name: t.bulk.select_font ?? t.bulk.select_option, value: "" },
      ...(settings.available_fonts ?? []).map((font: string) => ({
        name: font,
        value: font,
      })),
    ];

    columns.push({
      type: "custom",
      key: "properties___gcp_signature_font_family",
      label: t.bulk.signature_font ?? "Signature Font",
      DataEditor: makeDropdownEditor(fontValues),
      onValidate: () => true,
    });

    columns.push({
      type: "custom",
      key: "properties___gcp_message_font_family",
      label: t.bulk.message_font ?? "Message Font",
      DataEditor: makeDropdownEditor(fontValues),
      onValidate: () => true,
    });

  }

  const finalColumns = columns.map((col) => (
    col.type === "custom" && !("DataViewer" in col)
      ? { ...col, DataViewer: DefaultCellViewer }
      : col
  ));

  return {
    columns: finalColumns,
    columnTypeConfig: {},
  };

}
